import { useMutation, useQuery } from '@apollo/client';
import { Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PriceRuleFormDialog } from './PriceRuleFormDialog';
import { PriceRuleSubjectFilter, type SubjectFilterValue } from './PriceRuleSubjectFilter';
import {
  DEACTIVATE_PRICE_RULE_MUTATION,
  PRICE_RULE_SUBJECTS_LOOKUP,
  PRICE_RULES_QUERY,
} from './pricing.gql';

import { CrudTable, type CrudColumn } from '@/shared/crud/CrudTable';
import { CrudToolbar } from '@/shared/crud/CrudToolbar';
import { useCrudList } from '@/shared/crud/useCrudList';
import { formatCentsString } from '@/shared/lib/money';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { showToast } from '@/shared/ui/toast';
import { useAuthStore } from '@/stores/authStore';

interface PriceRule {
  id: string;
  subject_kind: string;
  subject_id: string;
  branch_id: string | null;
  priority: number;
  predicate_json: string;
  price_cents: string;
  note: string | null;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}

interface PriceRulesResponse {
  priceRules: {
    items: PriceRule[];
    page: { total: number; offset: number; limit: number; has_more: boolean };
  };
}

interface SubjectLookupItem {
  id: string;
  code: string;
  name: string;
}

interface SubjectsLookupResponse {
  products: { items: SubjectLookupItem[] };
  services: { items: SubjectLookupItem[] };
  kits: { items: SubjectLookupItem[] };
}

export function PriceRulesPage(): JSX.Element {
  const { t } = useTranslation();
  const list = useCrudList();
  const user = useAuthStore((s) => s.user);
  const can_manage = user?.permissions.includes('pricing.rules.manage') ?? false;

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PriceRule | null>(null);
  const [subject_filter, setSubjectFilter] = useState<SubjectFilterValue | null>(null);

  const { data, loading, refetch } = useQuery<PriceRulesResponse>(PRICE_RULES_QUERY, {
    variables: {
      offset: list.offset,
      limit: list.limit,
      search: list.search || undefined,
      subject_kind: subject_filter ? subject_filter.kind.toUpperCase() : undefined,
      subject_id: subject_filter?.id,
    },
    fetchPolicy: 'cache-and-network',
  });

  const onChangeSubjectFilter = (next: SubjectFilterValue | null): void => {
    setSubjectFilter(next);
    list.setOffset(0);
  };

  const { data: subjects_data } = useQuery<SubjectsLookupResponse>(PRICE_RULE_SUBJECTS_LOOKUP, {
    fetchPolicy: 'cache-first',
  });

  const subject_labels_by_kind: Record<string, Map<string, string>> = {
    product: new Map(
      (subjects_data?.products.items ?? []).map((s) => [s.id, `${s.code} — ${s.name}`]),
    ),
    service: new Map(
      (subjects_data?.services.items ?? []).map((s) => [s.id, `${s.code} — ${s.name}`]),
    ),
    kit: new Map((subjects_data?.kits.items ?? []).map((s) => [s.id, `${s.code} — ${s.name}`])),
  };

  const [deactivate] = useMutation(DEACTIVATE_PRICE_RULE_MUTATION);

  const onClickDeactivate = async (rule: PriceRule): Promise<void> => {
    try {
      await deactivate({ variables: { id: rule.id } });
      showToast({
        tone: 'success',
        title: t('alerts.update_success_title'),
        body: t('pricing.deactivated_body'),
      });
      await refetch();
    } catch (err) {
      showToast({
        tone: 'error',
        title: t('alerts.update_error_title'),
        body: err instanceof Error ? err.message : 'deactivate_failed',
      });
    }
  };

  const columns: CrudColumn<PriceRule>[] = [
    {
      key: 'subject',
      header_key: 'pricing.subject',
      render: (r) => {
        const label = subject_labels_by_kind[r.subject_kind.toLowerCase()]?.get(r.subject_id);
        return (
          <div className="space-y-0.5">
            <div className="flex items-center gap-2">
              <Badge variant="outline">{t(`pricing.kind_${r.subject_kind}`)}</Badge>
              {label ? (
                <span className="font-medium">{label}</span>
              ) : (
                <span className="font-mono text-xs text-muted-foreground">
                  {t('pricing.subject_unresolved')} ({r.subject_id.slice(0, 8)})
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'branch',
      header_key: 'pricing.branch',
      width: 'w-32',
      render: (r) => r.branch_id ? r.branch_id.slice(0, 8) : t('pricing.branch_global'),
    },
    {
      key: 'priority',
      header_key: 'pricing.priority',
      align: 'right',
      width: 'w-24',
      render: (r) => r.priority,
    },
    {
      key: 'price',
      header_key: 'pricing.price',
      align: 'right',
      width: 'w-32',
      render: (r) => formatCentsString(r.price_cents),
    },
    {
      key: 'predicate',
      header_key: 'pricing.predicate',
      render: (r) => (
        <code className="text-xs text-muted-foreground line-clamp-2 max-w-md break-all">
          {r.predicate_json}
        </code>
      ),
    },
    {
      key: 'status',
      header_key: 'common.status',
      align: 'center',
      width: 'w-28',
      render: (r) => (
        <Badge variant={r.status === 'active' ? 'success' : 'muted'}>
          {t(`pricing.status_${r.status}`)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header_key: 'common.actions',
      align: 'right',
      width: 'w-48',
      render: (r) =>
        can_manage ? (
          <div className="flex justify-end gap-1">
            <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(r)}>
              <Pencil className="h-4 w-4" aria-hidden />
              <span>{t('common.edit')}</span>
            </Button>
            {r.status === 'active' ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  void onClickDeactivate(r);
                }}
              >
                {t('pricing.deactivate')}
              </Button>
            ) : null}
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">{t('pricing.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('pricing.subtitle')}</p>
      </div>
      <CrudTable<PriceRule>
        rows={data?.priceRules.items ?? []}
        columns={columns}
        loading={loading}
        page={data?.priceRules.page ?? { total: 0, offset: 0, limit: list.limit, has_more: false }}
        onPageChange={list.setOffset}
        rowKey={(r) => r.id}
        empty_label_key="pricing.empty"
        toolbar={
          <CrudToolbar
            search={list.search}
            onSearchChange={list.setSearch}
            placeholder_key="pricing.note_search_placeholder"
            filters={
              <PriceRuleSubjectFilter value={subject_filter} onChange={onChangeSubjectFilter} />
            }
            primary_action={
              can_manage ? (
                <Button type="button" onClick={() => setCreating(true)}>
                  <Plus className="h-4 w-4" aria-hidden />
                  <span>{t('pricing.new_rule')}</span>
                </Button>
              ) : undefined
            }
          />
        }
      />
      {creating ? (
        <PriceRuleFormDialog
          onClose={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            showToast({
              tone: 'success',
              title: t('alerts.create_success_title'),
              body: t('pricing.created_body'),
            });
            await refetch();
          }}
        />
      ) : null}
      {editing ? (
        <PriceRuleFormDialog
          rule={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            showToast({
              tone: 'success',
              title: t('alerts.update_success_title'),
              body: t('pricing.updated_body'),
            });
            await refetch();
          }}
        />
      ) : null}
    </div>
  );
}
